"use client";

import { Bot, FileText, Globe, ExternalLink, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import Link from "next/link";
import { CreateBotDialog } from "@/components/bots/CreateBotDialog";
import { useBots } from "@/hooks/api/useBots";
import { formatDistanceToNow } from "date-fns";
import { useRouter } from "next/navigation";

export default function DashboardHome() {
  const { data: bots, isLoading } = useBots();
  const router = useRouter();

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-10">
      {/* Welcome Hero */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 py-6 border-b">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight">Welcome to AnyBot</h1>
          <p className="text-lg text-muted-foreground mt-2">
            Build intelligent, RAG-powered chatbots instantly using your own knowledge base.
          </p>
        </div>
        <div className="shrink-0">
          <CreateBotDialog trigger={
            <Button size="lg" className="shadow-lg">
              <Bot className="mr-2 w-5 h-5" />
              Create New Bot
            </Button>
          } />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Content Area: Your Bots */}
        <div className="md:col-span-2 space-y-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight mb-4">Your Bots</h2>
            {isLoading ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {[1, 2].map((i) => (
                  <Card key={i} className="animate-pulse h-32 bg-muted/20" />
                ))}
              </div>
    ) : bots && bots.length > 0 ? (
      <div className="grid gap-4 sm:grid-cols-2">
        {bots.map((bot) => (
          <Card 
            key={bot.id} 
            className="hover:border-primary/50 transition-colors group cursor-pointer relative overflow-hidden"
            onClick={() => router.push(`/dashboard/bots/${bot.id}/documents`)}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between text-xl">
                <span className="truncate">{bot.name}</span>
                <Bot className="h-5 w-5 text-primary/50" />
              </CardTitle>
              <CardDescription className="line-clamp-2 min-h-[40px]">
                {bot.description || "No description provided."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                Created {formatDistanceToNow(new Date(bot.created_at), { addSuffix: true })}
              </p>
              <div className="flex items-center justify-between mt-4">
                <Link href={`/dashboard/bots/${bot.id}/documents`} className="flex-1 mr-2" onClick={(e) => e.stopPropagation()}>
                  <Button variant="default" size="sm" className="w-full">
                    Knowledge Base
                  </Button>
                </Link>
                <Link href={`/dashboard/bots/${bot.id}/api-keys`} onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm">
                    Widget
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    ) : (
              <Card className="border-dashed border-2 bg-muted/10">
                <CardContent className="flex flex-col items-center justify-center p-12 text-center">
                  <div className="bg-primary/10 p-4 rounded-full mb-4">
                    <Bot className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">No Bots Found</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm">
                    You haven&apos;t created any bots yet. Start by creating your first AI assistant and uploading some knowledge.
                  </p>
                  <CreateBotDialog />
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Sidebar: Quick Actions & Status */}
        <div className="space-y-6">
          <Card className="bg-gradient-to-br from-primary/5 via-transparent to-transparent border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Activity className="mr-2 h-5 w-5 text-primary" />
                Quick Start Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 relative">
                <div className="absolute left-[11px] top-6 bottom-6 w-px bg-border z-0" />
                
                <div className="flex items-start gap-3 relative z-10">
                  <div className="h-6 w-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0 text-xs font-semibold">1</div>
                  <div className="pt-0.5">
                    <p className="font-medium text-sm">Create a Bot</p>
                    <p className="text-xs text-muted-foreground">Give it a name and persona.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 relative z-10">
                  <div className="h-6 w-6 rounded-full bg-background border flex items-center justify-center shrink-0 text-xs font-semibold">2</div>
                  <div className="pt-0.5">
                    <p className="font-medium text-sm">Add Knowledge</p>
                    <p className="text-xs text-muted-foreground">Upload PDFs or crawl websites.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 relative z-10">
                  <div className="h-6 w-6 rounded-full bg-background border flex items-center justify-center shrink-0 text-xs font-semibold">3</div>
                  <div className="pt-0.5">
                    <p className="font-medium text-sm">Deploy Widget</p>
                    <p className="text-xs text-muted-foreground">Embed the chat UI on your site.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Need Help?</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2">
              <Button variant="outline" className="justify-start w-full font-normal">
                <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                Documentation
                <ExternalLink className="ml-auto h-3 w-3 opacity-50" />
              </Button>
              <Button variant="outline" className="justify-start w-full font-normal">
                <Globe className="mr-2 h-4 w-4 text-muted-foreground" />
                API Reference
                <ExternalLink className="ml-auto h-3 w-3 opacity-50" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
